import { lazy, Suspense, useEffect, useState } from "react";
import { BlockDefinition } from "@softr-io/blocks-sdk";
import { useSearchLogic } from "./useSearchLogic";
import { DevtoolIcon } from "./DevtoolIcon";
import { useDefinitionStorage } from "@/definition-dev-tool/useDefinitionStorage";
import { ErrorBoundary } from "@softr-io/blocks-sdk/react";

const JsonEditor = lazy(() => import("json-edit-react").then((mod) => ({ default: mod.JsonEditor })));
type Props = {
    definition: BlockDefinition;
    originalDefinition: BlockDefinition;
    set: (def: BlockDefinition) => void;
};

const DefinitionDevTool = ({ definition, set, originalDefinition }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const { searchValue, deferredSearchValue, isSearching, handleSearchChange, clearSearch } = useSearchLogic(300);
    const { currentDefinition, isUsingStorageDefinition, setStorageDefinition, clearStorageDefinition } =
        useDefinitionStorage(definition, originalDefinition);

    const saveDefinition = (data: BlockDefinition) => {
        setStorageDefinition(data);
        set(data);
    };

    const toggleOpen = () => {
        setIsOpen((prev) => !prev);
    };

    const handleClose = () => {
        setIsOpen(false);
        clearSearch();
    };

    const clearDefinition = () => {
        set(originalDefinition);
        clearStorageDefinition();
    };

    // sync when the original definition changes
    useEffect(() => {
        set(originalDefinition);
    }, [set, originalDefinition]);

    return (
        <div className="devtools-container">
            <style>{getStyles()}</style>
            <button onClick={toggleOpen} className={"devtools-button"}>
                <DevtoolIcon />
            </button>
            {isOpen && (
                <div className="devtools-panel">
                    <div className="devtools-panel-buttons">
                        <button onClick={handleClose} className="devtools-close-button">
                            <img src="https://cdn-icons-png.flaticon.com/512/9068/9068678.png" alt="" />
                        </button>
                        {isUsingStorageDefinition ? (
                            <button onClick={clearDefinition} className="clear-button">
                                Clear Stored Definition
                            </button>
                        ) : null}
                        <div className="devtools-search-container">
                            <input
                                type="text"
                                className="devtools-search-input"
                                placeholder="Search..."
                                onChange={handleSearchChange}
                                value={searchValue}
                            />
                            {isSearching && (
                                <div className="devtools-search-spinner">
                                    <div className="spinner"></div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="devtools-textarea">
                        <Suspense>
                            <ErrorBoundary>
                                <JsonEditor
                                    data={currentDefinition}
                                    // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
                                    setData={saveDefinition}
                                    searchFilter="all"
                                    collapse={deferredSearchValue.trim() ? false : 2}
                                    searchDebounceTime={0}
                                    searchText={deferredSearchValue}
                                    showCollectionCount="when-closed"
                                />
                            </ErrorBoundary>
                        </Suspense>
                    </div>
                </div>
            )}
        </div>
    );
};

const getStyles = () => `
.devtools-container {
  position: relative;
}

.devtools-button {
  width: 48px;
  height: 48px;
  font-size: 12px;
  cursor: pointer;
  position: absolute;
  top: -5px;
  right: 5%;
  border-radius: 50%;
  z-index: 1000;
  transition: transform 0.2s ease-in-out;
  img {
     width: 48px;
  }
  &:hover {
     img {
     transform: scale(1.1);
     transition: transform 0.2s ease-in-out;
  }
  }
}

.devtools-panel {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  background: #191919;
  padding: 8px;
  border: 1px solid #f1e7a3;
  border-radius: 6px;
  position: fixed;
  top: 0;
  right: 0;
  height: 90vh;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 9999;
}

.devtools-textarea {
  font-size: 12px;
  color: #1f2937;
  border-radius: 4px;
  outline: none;
  width: 600px;
  height: 100%;
  overflow: scroll
}

.devtools-textarea:focus {
  border-color: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2);
}

.devtools-panel-buttons {
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

/* Search container with loading state */
.devtools-search-container {
  position: relative;
  display: flex;
  align-items: center;
}

/* Search input styles */
.devtools-search-input {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  color: #111827;
  font-size: 14px;
  min-width: 220px;
}

.devtools-search-input::placeholder {
  color: #9ca3af;
}

.devtools-search-input:focus {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2);
}

/* Loading spinner */
.devtools-search-spinner {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #f59e0b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Close button styles */
.devtools-close-button {
  width: 24px;
  height: 24px;
  cursor: pointer;
    background: transparent;
  img {
    width: 24px;
  }
}

.clear-button {
  background-color: #dc2626;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}
`;

export default DefinitionDevTool;
